import { PlatformPressable } from '@react-navigation/elements';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'react-native-toast-notifications';

import { ContactType } from '@components/Invite/Contact';
import ContactList from '@components/Invite/ContactList';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/RestyleIcon';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { ChatDrawerScreen } from '@components/Web/Drawer/WebDrawerContext';
import {
  useCreateChatMutation,
  useListContactUsersQuery,
  useListUsersLazyQuery,
  User,
} from '@graphql/generated';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';

const NewChat = () => {
  const { t } = useTranslation('models');
  const toast = useToast();
  const { data: listUsersData, loading: listUsersLoading } =
    useListContactUsersQuery({
      variables: { term: '' },
    });
  const [listUsers, { data: taskTagUsers }] = useListUsersLazyQuery();
  const { listContactUsers } = listUsersData || { listContactUsers: [] };
  // TA-10279: this query's `loading` was previously dropped entirely — the
  // contact list rendered empty (no spinner, no skeleton) for the whole
  // first fetch. 150ms minimum hold per the global skeleton rule.
  const showContactsSkeleton = useMinimumVisible(
    listUsersLoading && listContactUsers.length === 0
  );
  const { navigateToChatDrawer: navigateTo } = useAppNavigation();

  const [createChat, { data: createChatData, error: createChatError }] =
    useCreateChatMutation();

  // TA-10279 (QA fix): clicking a contact row here fired `createChat` with no
  // lock at all — a fast double-click (or clicking two different contacts in
  // a row) could kick off multiple concurrent chat creations. Mirrors the
  // proven `pendingMessageUserId` pattern from
  // src/screens/Contacts/Contacts.web.tsx (TA-10276).
  const [pendingContactId, setPendingContactId] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (!createChatData) return;
    setPendingContactId(undefined);
    navigateTo({
      screen: ChatDrawerScreen.details,
      activeChat: createChatData.createChat,
    });
  }, [createChatData]);

  useEffect(() => {
    if (!createChatError) return;
    toast.hideAll();
    toast.show('Failed to start chat, please try again later.', {
      duration: 3000,
    });
  }, [createChatError]);
  const [search, setSearch] = useState<string>('');

  const listUsersToContacts = convertUsersToContacts(
    listContactUsers as User[]
  );

  const listUsersToContactType = convertUsersToContacts(
    (taskTagUsers?.listUsers as User[]) || []
  ).filter((u) => !listUsersToContacts.some((c) => c.id === u.id));

  const sortedListUsersToContacts = listUsersToContacts.slice().sort((a, b) => {
    return a.firstName?.localeCompare(b.firstName);
  });
  const openChat = ({ firstName, lastName, id }: ContactType) => {
    // Re-entrancy guard: without this, clicking another row while the first
    // click's createChat is still in flight would fire a second concurrent
    // mutation (same rationale as Contacts.web.tsx's openChat).
    if (pendingContactId) return;
    setPendingContactId(id);
    const chatName = [firstName, lastName].filter(Boolean).join(' ');

    createChat({
      variables: {
        attributes: {
          name: chatName,
          userIds: [id],
        },
      },
      onError: () => setPendingContactId(undefined),
    });
  };

  useEffect(() => {
    search && listUsers({ variables: { term: search } });
  }, [search]);

  return (
    <Box flex={1}>
      <Box marginHorizontal='m' flexDirection='row' alignItems='center'>
        <SearchInput onTextChanged={setSearch} value={search} />
      </Box>
      {!search && (
        <Box m='m'>
          <PlatformPressable
            onPress={() =>
              navigateTo({ screen: ChatDrawerScreen.newGroupChat })
            }>
            <Box
              flexDirection='row'
              justifyContent='flex-start'
              alignItems='center'>
              <Icon name='Users' variant='m' backgroundColor='grey02' />
              <Text ml='m' variant='labelEmphasized'>
                {t('chat.labels.newGroupChat')}
              </Text>
            </Box>
          </PlatformPressable>
        </Box>
      )}
      <Box flex={1} marginTop='m'>
        <ContactList
          contacts={sortedListUsersToContacts}
          taskTagUsers={search ? listUsersToContactType : undefined}
          filterVal={search}
          onPress={(item: ContactType) => openChat(item)}
          loading={showContactsSkeleton}
          showLoadingSkeleton
        />
        {pendingContactId && (
          // TA-10279 (QA fix): rendered inside this flex:1 wrapper (not as a
          // sibling further up the tree) so its `position: absolute` offset
          // is relative to the list area, not the whole screen. Rendered
          // AFTER `<ContactList>` so it paints on top of the list rather
          // than being occluded by it (both are position:relative Views in
          // the same stacking context — later in tree order wins).
          <ActivityIndicatorLoading testID='new-chat-creating-indicator' />
        )}
      </Box>
    </Box>
  );
};

export default NewChat;
